<template>
    <div class="newspaper-details">
        <template v-if="categories.length === 0"> Brak wyników </template>
        <div
            v-for="(category, idx) in categories"
            :key="`newspaper-details-category-${idx}`"
            class="newspaper-details__category"
        >
            <h4 class="mb-4">
                {{ category.title }} ({{
                    categoryFiles(category.files).length
                }})
            </h4>

            <div class="newspaper-details__files">
                <div
                    v-for="(file, fIdx) in categoryFiles(category.files)"
                    :key="`newspaper-details-file-${fIdx}`"
                    class="newspaper-details__file"
                >
                    <figure class="newspaper-details__file-fig">
                        <img
                            v-if="file.preview"
                            :src="file.preview"
                            :alt="file.filename"
                            @click="openModal(file, fIdx)"
                        />
                        <a-icon
                            v-else-if="file.filetype === 'spreadsheet' || file.filetype === 'xlsx'"
                            class="newspaper-details__no-img text-primary"
                            icon="Xls"
                        />
                        <a-icon
                            v-else-if="file.filetype === 'zip'"
                            class="newspaper-details__no-img text-primary"
                            icon="Zip"
                        />
                        <a-icon
                            v-else
                            class="newspaper-details__no-img text-primary"
                            icon="NoImg"
                        />
                    </figure>

                    <h5 class="mb-0" v-text="file.name" />

                    <p
                        v-if="file.subname"
                        v-text="file.subname"
                        class="typo--sm mb-0"
                    />

                    <the-btn
                        class="btn-custom-gray mx-auto mt-auto"
                        :icon="loader === file.name ? 'Spinner' : 'Download'"
                        :disabled="!!loader"
                        text=""
                        @click.native="downloadFile(file)"
                    />
                </div>
            </div>
        </div>

        <modal-preview
            v-if="previewFile"
            class="newspaper-details__modal"
            :active-slide-idx="activeSlideIdx"
            @close="previewFile = null; renderLoader = false"
        >
            <template #header>
                <h2 class="h4 mb-0 text-center newspaper-details__slide-title">
                    {{ previewFile.filename }}
                </h2>
            </template>

            <div class="newspaper-details">
                <div class="newspaper-details__slide">
                    <page-preloader
                        v-if="renderLoader"
                        status="loading"
                        class="ml-3"
                    />
                    <vue-pdf-embed
                        ref="pdfRef"
                        :source="previewFile.url"
                        @rendered="onPdfRendered"
                    />
                </div>
            </div>

            <template #footer>
                <the-btn
                    class="btn-custom-gray"
                    :icon="loader === previewFile.name ? 'Spinner' : 'Download'"
                    :disabled="!!loader"
                    text=""
                    @click.native="downloadFile(previewFile)"
                />
            </template>
        </modal-preview>
    </div>
</template>

<script>
import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed'

export default {
    name: 'NewspaperDetails',

    components: {
        VuePdfEmbed
    },

    data() {
        return {
            loader: false,
            previewFile: null,
            renderLoader: false
        }
    },

    props: {
        material: {
            type: Object,
            required: true
        }
    },

    computed: {
        materialTitle() {
            const { material } = this
            return `Cykl ${material.series} - ${material.franchise} ${material.format}`
        },

        selectedFilters() {
            return this.$store.getters.getNewspaperMaterialsFilters
        },

        categories() {
            // group files by type
            const files = (this.material.files || []).reduce((acc, item) => {
                if (!acc[item.type]) {
                    acc[item.type] = []
                }

                acc[item.type].push(item)
                return acc
            }, {})

            const filters = this.selectedFilters
            const categories = filters.category.value || []

            return Object.entries(files)
                .filter(([key, value]) => {
                    const filesLength = this.categoryFiles(value).length

                    if (!filesLength) {
                        return false
                    }

                    if (categories.length) {
                        return categories.includes(key)
                    }

                    return true
                })
                .map(([key, value]) => {
                    return {
                        title: key,
                        files: value
                    }
                })
        }
    },

    methods: {
        async downloadFile(file) {
            if (this.loader) {
                return
            }

            this.loader = file.name
            try {
                await this.$functions.downloadFile(file.url, file.filename)
            } catch (err) {
                this.$store.dispatch('addNotification', {
                    type: 'danger',
                    content: 'Wystąpił błąd. Spróbuj ponownie później.'
                })
                console.log(err)
            } finally {
                this.loader = null
            }
        },

        categoryFiles(files) {
            const filters = this.selectedFilters
            const filename = filters.filename.value
            const subname = filters.subname.value

            if (filename) {
                files = files.filter(file =>
                    file.name.toLowerCase().includes(filename.toLowerCase())
                )
            }

            if (subname.length) {
                const normalizeString = str =>
                    str
                        .toLowerCase()
                        .normalize('NFD')
                        .replace(/[\u0300-\u036f]/g, '')

                return files.filter(file => {
                    const fileTypes = file.subname.trim().split(', ')

                    return subname.some(t =>
                        fileTypes.some(ft => {
                            return normalizeString(ft).startsWith(
                                normalizeString(t)
                            )
                        })
                    )
                })
            }

            return files
        },

        updateFilters() {
            let files = Object.entries(this.material.files).reduce(
                (acc, [key, value]) => {
                    acc[key] = this.categoryFiles(value)
                    return acc
                },
                {}
            )

            if (Object.keys(files).length === 0) {
                files = {}
            }

            this.$emit('update-filters', files)
        },

        openModal(file, idx) {
            this.previewFile = file
            this.activeSlideIdx = idx
            this.renderLoader = true
        },

        closeModal() {
            this.previewFile = null
            this.renderLoader = false
        },

        onPdfRendered() {
            this.renderLoader = false

            const pdf = this.$refs.pdfRef.$el
            pdf.style.visibility = 'visible'

            this.adjustHeight()
        },

        adjustHeight() {
            const pdf = this.$refs.pdfRef.$el
            pdf.style.height = 'auto'
            const pdfHeight = pdf.clientHeight

            const windowHeight = window.innerHeight * 0.95
            const modalHeader = document.querySelector('.modal-preview__header')
            const modalContent = document.querySelector('.modal-preview__content')
            const modalFooter = document.querySelector('.modal-preview__footer')
            const pdfContainer = windowHeight - modalHeader.clientHeight - modalFooter.clientHeight

            if (pdfHeight > pdfContainer) {
                modalContent.style.height = `${pdfContainer}px`
            }
        }
    },

    mounted() {
        document.addEventListener('resize', this.adjustHeight)
    },

    beforeDestroy() {
        document.removeEventListener('resize', this.adjustHeight)
    }
}
</script>

<style lang="scss">
@import '@/styles/_custom.scss';
@import '~bootstrap/scss/mixins';

.newspaper-details {
    &__category {
        border: 1px solid var(--color-gray-200);
        border-radius: var(--border-radius);
        background-color: var(--color-white);
        padding: 1.5rem;

        + .newspaper-details__category {
            margin-top: 1rem;
        }
    }

    &__files {
        --gap-x: 1rem;
        --gap-y: 2.5rem;
        display: flex;
        gap: var(--gap-y) var(--gap-x);
        flex-wrap: wrap;
    }

    &__file {
        --items-per-row: 4;
        --size: calc(100% / var(--items-per-row));
        --gap-size: calc(
            (var(--gap-x) * (var(--items-per-row) - 1)) / var(--items-per-row)
        );
        flex: 0 1 calc(var(--size) - var(--gap-size));
        display: flex;
        flex-direction: column;
        gap: 0.25rem;

        &:hover {
            img {
                transform: scale(1.2);
            }
        }
    }

    &__file-fig {
        width: 100%;
        height: 320px;
        border: 1px solid var(--color-primary);
        border-radius: var(--border-radius);
        overflow: hidden;
        display: flex;
        justify-content: center;
        margin-bottom: 0.75rem;

        img {
            cursor: pointer;
            object-fit: cover;
            object-position: center;
            width: 100%;
            height: 100%;
            transform: scale(1);
            transition: 0.3s ease-in-out;
        }
    }

    &__no-img {
        --size: 6rem;
        &.a-icon {
            align-self: center;
            width: var(--size);

            svg {
                width: var(--size);
                height: var(--size);
            }
        }
    }

    &__slide {
        text-align: center;
    }

    &__modal {
        &.modal-preview {
            --preview-width: calc(1290px - 4rem);
        }

        .modal-preview__footer {
            text-align: center;
        }

        .vue-pdf-embed {
            height: 0;
            visibility: hidden;
            width: var(--preview-width);
            max-width: 100%;

            &__page {
                canvas {
                    max-width: 100%;
                    height: auto !important;
                }
            }

            div + div {
                margin-top: 1.5rem;
            }
        }
    }

    @include media-breakpoint-down(md) {
        &__file {
            --items-per-row: 3;
        }
    }

    @include media-breakpoint-down(sm) {
        &__file {
            --items-per-row: 2;
        }
    }

    @include media-breakpoint-down(ms) {
        &__file {
            --items-per-row: 1;
            max-width: 260px;
            margin-left: auto;
            margin-right: auto;
        }
    }
}
</style>
